import moment, { Moment, MomentInput } from 'moment-timezone';
import { IntlDateFormatName, IntlDateFormatter } from '@blocs.i18n';
import { gqlTimeZone } from '@minecraft.graphql-types';
import dateFormat from './constants';

type TZFormat =
  | gqlTimeZone
  | {
      abbreviation?: string;
      standardName?: string;
    };

export function addTimeZone(date = '', tzAbbreviation?: string): string {
  if (!tzAbbreviation) return date;

  return `${date} ${tzAbbreviation}`;
}

export function getLocalTimeZone(): TZFormat {
  const standardName = moment.tz.guess();

  return { standardName, abbreviation: moment.tz(standardName).zoneName() };
}

export function getDate(date: MomentInput, format?: dateFormat, tz?: TZFormat): string | null {
  if (!date) return null;

  return addTimeZone(moment.parseZone(date).format(format), tz?.abbreviation);
}

export function getDateWithTimeZone(
  date: MomentInput,
  format: dateFormat,
  tz: TZFormat,
  skipTzAbbreviation = false
): string | null {
  if (!date) return null;

  const returnedDate = moment.tz(date, tz.standardName).format(format);

  return skipTzAbbreviation ? returnedDate : addTimeZone(returnedDate, tz?.abbreviation);
}

export function getDateWithLocalTimeZone(
  date: MomentInput,
  format: dateFormat,
  skipTzAbbreviation = false
): string | null {
  return getDateWithTimeZone(date, format, getLocalTimeZone(), skipTzAbbreviation);
}

export function convertDateFromTzToTz(
  date: MomentInput,
  format: dateFormat,
  sourceTz: TZFormat,
  destTz: TZFormat,
  skipTzAbbreviation = false
): string | null {
  if (!sourceTz?.standardName || !destTz?.standardName) {
    // if the timezone is undefined then moment will just default
    // to UTC which will give the wrong date, better to
    // return nothing so it's easier to catch the bug
    return '';
  }

  // strip UTC "Z" timezone off and cast the date to the source timezone
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  const sourceDate = moment.tz(date.toString().replace('Z', ''), sourceTz.standardName);

  // pass the date that is set to the correct local source time,
  // then pass into getDateWithTimeZone which will format it to the destination TZ
  return getDateWithTimeZone(sourceDate.toISOString(), format, destTz, skipTzAbbreviation);
}

export function convertIntlDateToTz(date: MomentInput, sourceTzStandardName: string): string | null {
  if (!sourceTzStandardName) {
    // if the timezone is undefined then moment will just default
    // to UTC which will give the wrong date, better to
    // return nothing so it's easier to catch the bug
    return '';
  }

  // strip UTC "Z" timezone off and cast the date to the source timezone
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return moment.tz(date.toString().replace('Z', ''), sourceTzStandardName).toISOString();
}

export function getIntlMultiFormatDate(
  formatIntlDate: IntlDateFormatter,
  date: string,
  formats: IntlDateFormatName[],
  timeZoneStandardName?: string,
  destTimeZoneStandardName?: string
): string[] {
  return formats.map((format) => {
    if (!date || !timeZoneStandardName || !destTimeZoneStandardName) {
      return '-';
    }

    return formatIntlDate({
      date,
      timeZoneStandardName,
      destTimeZoneStandardName,
      format,
    });
  });
}

export function isValidDate(dateString: string) {
  if (typeof dateString !== 'string') {
    return false;
  }

  const date = moment(dateString);
  return date.isValid();
}

export const parseTimeToDateString = (timeStr: string) => {
  if (!timeStr) {
    return 'Invalid date';
  }
  const timeParts = timeStr?.split(':');
  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1], 10);

  if (minutes > 59 || hours > 23 || minutes < 0 || hours < 0) {
    return 'Invalid date';
  }
  if (timeParts?.length !== 2 || Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 'Invalid date';
  }

  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);

  return date.toUTCString();
};

/** given a value, that can be parsed to a date, will return an properly formatted ISO string
 * @param inputText - the date string to parse, if the value is not a string or cannot be parsed to a valid value it will return an empty string ''
 * @param format - the format to use for parsing the date, defaults to 'MM/DD/YYYY'
 */
export const parseTextToISODateStr = ({ inputText, format }: { inputText: string; format?: string }): string => {
  if (typeof inputText !== 'string') {
    return '';
  }

  const parsedMoment = moment.utc(inputText, format ?? moment.ISO_8601);
  return parsedMoment.isValid() ? parsedMoment.toISOString() : '';
};

/** given an ISO date string, returns another ISO date string where the time portion is set to the start of the day
 * if no date is provided, the current date is used
 * @example (assuming date is May 8, 2021) getStartOfDay() // '2021-05-08T00:00:00.000Z'
 * @example getStartOfDay('2021-08-01T12:00:00.000Z') // '2021-08-01T00:00:00.000Z'
 */
export const getStartOfDay = (isoDateString?: string): string => {
  return moment.utc(isoDateString).startOf('day').toISOString();
};

/** given an ISO date string, returns another ISO date string where the time portion is set to the end of the day
 * if no date is provided, the current date is used
 * @example (assuming date is May 8, 2021) getEndOfDay() // '2021-05-08T23:59:99.999Z'
 * @example getEndOfDay('2021-08-01T12:00:00.000Z') // '2021-08-01T23:59:99.999Z'
 */
export const getEndOfDay = (isoDateString?: string): string => {
  return moment.utc(isoDateString).endOf('day').toISOString();
};

type TimeUnit = 'year' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second';

/**
 * given an ISO date string, returns true if the date is in the past
 * @param isoDateString - the date to check
 * @param includeSame - if true, will return true if the date matches the current date, false by default
 * @param pastCutoff - an optional object to specify a cutoff for the past date, if provided, the date must be after this cutoff
 *
 * @example
 * // current date = '2021-08-01T12:00:00.000Z'
 * isDateInPast({ isoDateString: '2021-07-31T12:00:00.000Z' }) // true
 * isDateInPast({ isoDateString: '2021-08-01T12:00:00.000Z' }) // false
 * isDateInPast({ isoDateString: '2021-08-01T12:00:00.000Z', includeSame: true }) // true
 * isDateInPast({ isoDateString: '2021-07-31T12:00:00.000Z', pastCutoff: { count: 2, unit: 'day' } }) // false
 * isDateInPast({ isoDateString: '2021-07-01T12:00:00.000Z', pastCutoff: { count: 1, unit: 'hour' } }) // false
 * */
export const isDateInPast = ({
  isoDateString,
  includeSame = false,
  pastCutoff = null,
}: {
  isoDateString: string;
  includeSame?: boolean;
  pastCutoff?: { count: number; unit: TimeUnit };
}): boolean => {
  const dateMoment = moment.utc(isoDateString);
  const currentMoment = moment.utc();
  const pastCutoffMoment = pastCutoff ? moment.utc().subtract(pastCutoff.count, pastCutoff.unit) : null;

  if (includeSame) {
    return (
      dateMoment.isSameOrBefore(currentMoment) && (pastCutoffMoment ? dateMoment.isSameOrAfter(pastCutoffMoment) : true)
    );
  }

  return dateMoment.isBefore(currentMoment) && (pastCutoffMoment ? dateMoment.isAfter(pastCutoffMoment) : true);
};

/**
 * given as ISO date string, returns true if the date is in the future
 * @param isoDateString - the date to check
 * @param includeSame - if true, will return true if the date matches the current date, false by default
 * @param futureCutoff - an optional object to specify a cutoff for the future date, if provided, the date must be before this cutoff
 *
 * @example
 * // current date = '2021-08-01T12:00:00.000Z'
 * isDateInFuture({ isoDateString: '2021-08-02T12:00:00.000Z' }) // true
 * isDateInFuture({ isoDateString: '2021-08-01T12:00:00.000Z' }) // false
 * isDateInFuture({ isoDateString: '2021-08-01T12:00:00.000Z', includeSame: true }) // true
 * isDateInFuture({ isoDateString: '2021-08-08T12:00:00.000Z', futureCutoff: { count: 5, unit: 'day' } }) // false
 * isDateInFuture({ isoDateString: '2021-08-08T12:00:00.000Z', futureCutoff: { count: 10, unit: 'day' } }) // true
 * */
export const isDateInFuture = ({
  isoDateString,
  includeSame = false,
  futureCutoff = null,
}: {
  isoDateString: string;
  includeSame?: boolean;
  futureCutoff?: { count: number; unit: TimeUnit };
}): boolean => {
  const dateMoment = moment.utc(isoDateString);
  const currentMoment = moment.utc();
  const futureCutoffMoment = futureCutoff ? moment.utc().add(futureCutoff.count, futureCutoff.unit) : null;

  if (includeSame) {
    return (
      dateMoment.isSameOrAfter(currentMoment) &&
      (futureCutoffMoment ? dateMoment.isSameOrBefore(futureCutoffMoment) : true)
    );
  }

  return dateMoment.isAfter(currentMoment) && (futureCutoffMoment ? dateMoment.isBefore(futureCutoffMoment) : true);
};

/** given two dates in ISO format, this will return ISO strings for each date from the start date to the end date
 * @param startDate - the start date in ISO format
 * @param endDate - the end date in ISO format
 * @example
 * const startDate = '2021-08-01T12:00:00.000Z';
 * const endDate = '2021-08-04T12:00:00.000Z';
 * console.log(enumerateDaysBetweenDates(startDate, endDate));
 * // ['2021-08-01T00:00:00.000Z', '2021-08-02T00:00:00.000Z', '2021-08-03T00:00:00.000Z', '2021-08-04T00:00:00.000Z']
 */
export const enumerateDaysBetweenDates = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
  withOffset?: boolean;
}): string[] => {
  const startMoment = moment.utc(startDate).startOf('day');
  const endMoment = moment.utc(endDate).endOf('day');
  const outputIsoStrings = [];

  while (startMoment.isSameOrBefore(endMoment)) {
    outputIsoStrings.push(startMoment.startOf('day').toISOString());
    startMoment.add(1, 'days');
  }

  return outputIsoStrings;
};

export const getAgeInYears = (date: string): number => {
  if (!isValidDate(date)) {
    return undefined;
  }

  return moment.utc().diff(date, 'years');
};

/**
 * Adds a specified amount of time to the current date and returns the result as an ISO string.
 * @param date - The date to which the time will be added (in ISO format). If not provided, the current date is used.
 * @param amount - The amount of time to add.
 * @param unit - The unit of time to add.
 * @returns {string} The resulting date as an ISO string.
 *
 * @example
 * Add 1 day to the current date
 * // current date = '2022-01-01T00:00:00.000Z'
 * const result = addToDate({ amount: 1, unit: 'day' });
 * console.log(result); // Output: "2022-01-02T00:00:00.000Z"
 */
export const addToDate = ({
  date,
  amount,
  unit,
}: {
  date?: string;
  amount: number;
  unit: moment.unitOfTime.DurationConstructor;
}): string => {
  return moment.utc(date).add(amount, unit).toISOString();
};

/**
 * Subtracts a specified amount of time from the current date and returns the result as an ISO string.
 * @param date - The date to which the time will be subtracted (in ISO format). If not provided, the current date is used.
 * @param amount - The amount of time to subtract.
 * @param unit - The unit of time to subtract.
 * @returns {string} The resulting date as an ISO string.
 *
 * @example
 * Subtract 1 day from the current date
 * // current date = '2022-01-02T00:00:00.000Z'
 * const result = subtractFromDate({ amount: 1, unit: 'day' });
 * console.log(result); // Output: "2022-01-01T00:00:00.000Z"
 */
export const subtractFromDate = ({
  date,
  amount,
  unit,
}: {
  date?: string;
  amount: number;
  unit: moment.unitOfTime.DurationConstructor;
}): string => {
  return moment.utc(date).subtract(amount, unit).toISOString();
};

const isNil = (val: unknown) => val === undefined || val === null;

export type DateType = string | Date | moment.Moment;

/**
 * Normalizes a date value to a UTC moment.
 * Strings are parsed as UTC. Date and Moment values keep their calendar
 * components and are treated as UTC, so a local June 2 stays June 2.
 */
export const toUtcMoment = (date?: DateType | null): Moment | null => {
  if (isNil(date)) {
    return null;
  }

  if (typeof date === 'string') {
    return moment.utc(date, moment.ISO_8601, true);
  }

  if (date instanceof Date) {
    return moment.utc(moment(date).utc(true).format());
  }

  if (moment.isMoment(date)) {
    return date.clone().utc(true);
  }

  return null;
};

/**
 * Formats a date as month and day. Includes the year when the date is not in the current year.
 */
export const formatDateByCurrentYear = (
  date: DateType | null | undefined,
  formatIntlDate: IntlDateFormatter
): string | null => {
  const momentDate = toUtcMoment(date);

  if (!momentDate?.isValid()) {
    return null;
  }

  const format: IntlDateFormatName = momentDate.isSame(moment(), 'year') ? 'MONTH_DAY' : 'MONTH_DAY_YEAR';

  return formatIntlDate({ date: momentDate.toISOString(), format });
};

export const getFormattedDate = (date: DateType, formatIntlDate: IntlDateFormatter): string => {
  let momentDate = null;

  if (isNil(date)) return momentDate;

  if (typeof date === 'string') {
    momentDate = moment.utc(date);
  }

  if (date instanceof Date) {
    momentDate = moment.utc(moment(date).utc(true).format());
  }

  if (date instanceof moment) {
    momentDate = (date as Moment).utc(true);
  }

  return formatIntlDate({ date: momentDate?.toISOString(), format: 'MONTH_DAY' });
};

export const calculateTimeLeft = (endsAt: Date) => {
  const now = new Date();
  const target = new Date(endsAt);
  const diff = Math.max(target.getTime() - now.getTime(), 0);

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return { days, hours, minutes, seconds };
};

export const getCurrentDateTime = () => moment.utc().toISOString();

/**
 * @param startDate - an ISO date string representing the start date for the duration calculation, if not provided, the current date will be used
 * @param endDate - an ISO date string representing the end date for the duration calculation, if not provided, the current date will be used
 * @param roundAs - how to round the `as*` totals. Defaults to `round`. Use `floor` for full elapsed-unit boundaries
 *   (e.g. experiment day cutoffs where day N should not begin until N * 24 hours have passed).
 * @returns object with enumerated duration values between the two dates
 */
export const getDurationBetweenDates = ({
  startDate,
  endDate,
  roundAs = 'round',
}: {
  startDate?: string;
  endDate?: string;
  roundAs?: 'round' | 'floor';
} = {}) => {
  const startMoment = startDate ? moment.utc(startDate) : moment.utc();
  const endMoment = endDate ? moment.utc(endDate) : moment.utc();
  const pureTimeDiff = endMoment.diff(startMoment);
  const duration = moment.duration(pureTimeDiff);
  const roundFn = roundAs === 'floor' ? Math.floor : Math.round;

  return {
    years: duration.years(),
    months: duration.months(),
    days: duration.days(),
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
    asYears: roundFn(duration.asYears()),
    asMonths: roundFn(duration.asMonths()),
    asDays: roundFn(duration.asDays()),
    asHours: roundFn(duration.asHours()),
    asMinutes: roundFn(duration.asMinutes()),
    asSeconds: roundFn(duration.asSeconds()),
    rawTime: pureTimeDiff,
  };
};

/**
 * takes 2 ISO date strings: one for the date and one for the time
 * @param datePart - an ISO date string representing the date portion ("2026-04-17T00:00:00.000Z")
 * @param timePart - an ISO date string representing the time portion ("1970-01-01T18:00:00.000Z")
 * @returns an ISO date string representing the merged date and time ("2026-04-17T18:00:00.000Z")
 *
 * this is used in the casting scheduler to merge the time portion of a timeframe or timeslot date
 * with the date portion of the schedule date b/c the timeframe & timeslot times are all set to the epoch date ("1970-01-01T...")
 *
 * if either datePart or timePart is falsy, or if they cannot be parsed as valid dates strings
 * then the function will return null.
 */
export const mergeDateAndTime = ({ datePart, timePart }: { datePart?: string; timePart?: string }) => {
  if (!datePart || !timePart || !isValidDate(datePart) || !isValidDate(timePart)) {
    return null;
  }

  const date = moment.utc(datePart);
  const time = moment.utc(timePart);

  date.set({
    hour: time.hour(),
    minute: time.minute(),
    second: time.second(),
    millisecond: time.millisecond(),
  });

  return date.toISOString();
};
